import { createSelector } from 'reselect';
import { IStore, MockCustomers, UiStatus } from '../../../common/type';
import { is } from '../../../util/is';
import {
  CustomerDiscountType,
  IAdsDiscount,
  IAdsInfo,
  IBulkBonusPackDiscount,
  IBulkPriceOverrideDiscount
} from './ads-checkout-type';

const selectModuleState = (state: IStore) => state.adsCheckout;

export const selectCustomerId = (state: IStore) => selectModuleState(state).model.customerId;

export const selectCustomerName = (state: IStore) =>
  MockCustomers.filter(customer => customer.id === selectCustomerId(state)).map(
    customer => customer.name
  )[0];

export const selectAdsTypeInfo = (state: IStore) => selectModuleState(state).model.adsTypeInfo;

export const selectAds = (state: IStore) => selectModuleState(state).model.ads;

export const selectAdInfo = (state: IStore, index: number) => selectAds(state)[index];

export const selectIsBusy = (state: IStore) => selectModuleState(state).ui.status === UiStatus.Busy;

export const selectIsError = (state: IStore) =>
  selectModuleState(state).ui.status === UiStatus.Error;

export const selectErrorMessage = (state: IStore) => selectModuleState(state).ui.errorMessage;

const selectAdsOfSimilarType = (state: IStore, adIndex: number) =>
  selectAds(state).filter((ad, index, ads) => ad.typeId === ads[adIndex].typeId);

const selectDiscounts = (state: IStore) => selectModuleState(state).model.discounts;

export const selectAdBasePrice = (state: IStore, index: number) => {
  const adInfo = selectAdInfo(state, index);
  const adsTypeInfo = selectModuleState(state).model.adsTypeInfo;

  const basePrice = adsTypeInfo
    .filter(adType => adType.idKey === adInfo.typeId)
    .map(adType => adType.basePrice);

  return basePrice[0];
};

export const getBulkPriceOverrideDiscountedPrice = (
  discount: IBulkPriceOverrideDiscount,
  adsWithSameType: IAdsInfo[]
) => (adsWithSameType.length >= discount.minQty ? discount.discountedPrice : undefined);

export const getBulkBonusPackDiscountedPrice = (
  recordId: number,
  discount: IBulkBonusPackDiscount,
  adsWithSameType: IAdsInfo[]
) => {
  const adsIndex = adsWithSameType.findIndex(ad => ad.id === recordId);

  const freeQty = Math.floor(adsWithSameType.length / discount.packSize) * discount.freeQty;

  return adsIndex >= adsWithSameType.length - freeQty ? 0 : undefined;
};

export const getDiscountedPrice = (
  adInfo: IAdsInfo,
  discounts: IAdsDiscount[],
  adsWithSameType: IAdsInfo[]
) =>
  discounts.filter(discount => discount.adsTypeId === adInfo.typeId).map(({ discount }) => {
    switch (discount.type) {
      case CustomerDiscountType.PriceOverride:
        return discount.discountedPrice;

      case CustomerDiscountType.BulkPriceOverride:
        return getBulkPriceOverrideDiscountedPrice(discount, adsWithSameType);

      case CustomerDiscountType.BulkBonusPack:
        return getBulkBonusPackDiscountedPrice(adInfo.id, discount, adsWithSameType);

      default:
        return undefined;
    }
  })[0];

export const createDiscountedPriceSelector = () =>
  createSelector(selectAdInfo, selectDiscounts, selectAdsOfSimilarType, getDiscountedPrice);

export const selectTotal = (state: IStore) => {
  const selectDiscountedPrice = createDiscountedPriceSelector();
  const ads = selectAds(state);

  return ads.reduce((total, ad, index) => {
    const discountedPrice = selectDiscountedPrice(state, index);

    return is.Nil(discountedPrice)
      ? total + selectAdBasePrice(state, index)
      : total + (discountedPrice as number);
  }, 0);
};



// WEBPACK FOOTER //
// ./src/modules/ads-checkout/parts/ads-checkout-selector.ts